import { useState, useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/shared/lib/toast';
import { ApiClientError } from '@/shared/lib/api';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { controlsService } from '@/shared/api/services/controls.service';
import type { Control, Angle } from '@/types/concept';
import type { RenderControl } from '@/types/render';
import { FileTypes } from '@/shared/constants/file-types';
import {
  filterValidControls,
  countUnusedControls,
  hasValidControlFile,
} from '../lib/control-validation';
import { CONTROL_LIMITS } from '../constants';

interface UseControlManagementProps {
  angle: Angle | null;
  rawControls: Control[];
  selectedControlIds: Array<RenderControl | string>; // Support both formats during transition
  onControlsChange: (controls: RenderControl[]) => void;
  onControlsUpdate?: (controls: Control[]) => void;
}

interface ControlManagementState {
  isEditingControl: boolean;
  editingControlBase: Control | null;
  isCreatingNewControl: boolean;
  deleteConfirmation: { control: Control; isUsed: boolean } | null;
  deleting: boolean;
}

export function useControlManagement({
  angle,
  rawControls,
  selectedControlIds,
  onControlsChange,
  onControlsUpdate,
}: UseControlManagementProps) {
  const t = useTranslations('render.settings.control');
  const tErrors = useTranslations('common.errors');
  const queryClient = useQueryClient();

  // Keep the settings-panel sketch list (useProjectControls) and any per-angle
  // control queries in sync after a create/update/delete. Without this, the
  // TanStack Query cache backing the list stays stale (staleTime 2min) and the
  // new sketch only shows up on page refresh / window refocus — the parent
  // onControlsUpdate callback only refreshes the concept, not these queries.
  const invalidateControlLists = useCallback(
    (projectId?: string | null) => {
      if (angle) {
        queryClient.invalidateQueries({
          queryKey: cacheKeys.controls.listByAngle(angle.id),
        });
      }
      if (projectId) {
        queryClient.invalidateQueries({
          queryKey: cacheKeys.controls.listByProject(projectId),
        });
      }
    },
    [queryClient, angle]
  );
  // Local state only for UI-specific concerns
  const [state, setState] = useState<ControlManagementState>({
    isEditingControl: false,
    editingControlBase: null,
    isCreatingNewControl: false,
    deleteConfirmation: null,
    deleting: false,
  });

  // Filter valid controls from props, sort by created_at descending (newest first)
  const controls = useMemo(() => {
    const valid = filterValidControls(rawControls);
    return valid.sort(
      (a, b) =>
        new Date(b.attributes.created_at).getTime() -
        new Date(a.attributes.created_at).getTime()
    );
  }, [rawControls]);

  // Calculate unused controls count
  const unusedControlsCount = useMemo(() => {
    return countUnusedControls(controls);
  }, [controls]);

  // Find currently selected control
  const selectedControl = useMemo(() => {
    return controls.find(
      (c) =>
        selectedControlIds.some((item) =>
          typeof item === 'string' ? item === c.id : item.id === c.id
        ) &&
        hasValidControlFile(c)
    );
  }, [controls, selectedControlIds]);

  const handleCreateControl = useCallback(() => {
    if (!angle) return;

    // Check if limit is reached before opening editor
    if (unusedControlsCount >= CONTROL_LIMITS.MAX_UNUSED_CONTROLS) {
      toast.warning(t('limitWarningTitle'), {
        description: t('limitWarningDescription', {
          max: CONTROL_LIMITS.MAX_UNUSED_CONTROLS,
        }),
        duration: 5000,
      });
      return;
    }

    // Open editor with empty canvas (no base control)
    setState((prev) => ({
      ...prev,
      editingControlBase: null,
      isCreatingNewControl: true,
      isEditingControl: true,
    }));
  }, [angle, unusedControlsCount, t]);

  const handleEditControl = useCallback(
    (forceNewVersion = false) => {
      if (selectedControl && !forceNewVersion) {
        // Edit existing selected control
        setState((prev) => ({
          ...prev,
          editingControlBase: selectedControl,
          isCreatingNewControl: false,
          isEditingControl: true,
        }));
      } else if (selectedControl && forceNewVersion) {
        // Save as new version
        if (unusedControlsCount >= CONTROL_LIMITS.MAX_UNUSED_CONTROLS) {
          toast.warning(t('limitWarningTitle'), {
            description: t('limitWarningDescription', {
              max: CONTROL_LIMITS.MAX_UNUSED_CONTROLS,
            }),
            duration: 5000,
          });
          return;
        }
        setState((prev) => ({
          ...prev,
          editingControlBase: selectedControl,
          isCreatingNewControl: true,
          isEditingControl: true,
        }));
      } else {
        // No existing control - create new
        handleCreateControl();
      }
    },
    [selectedControl, handleCreateControl, unusedControlsCount, t]
  );

  const handleEditControlFor = useCallback(
    (control: Control, forceNewVersion = false) => {
      // Save-as-new must respect the unused-control cap
      if (
        forceNewVersion &&
        unusedControlsCount >= CONTROL_LIMITS.MAX_UNUSED_CONTROLS
      ) {
        toast.warning(t('limitWarningTitle'), {
          description: t('limitWarningDescription', {
            max: CONTROL_LIMITS.MAX_UNUSED_CONTROLS,
          }),
          duration: 5000,
        });
        return;
      }
      setState((prev) => ({
        ...prev,
        editingControlBase: control,
        isCreatingNewControl: forceNewVersion,
        isEditingControl: true,
      }));
    },
    [unusedControlsCount, t]
  );

  const handleSaveEditedControl = useCallback(
    async (blob: Blob) => {
      if (!angle) return;

      try {
        let newControl: Control;
        let action: 'updated' | 'created';

        if (!state.isCreatingNewControl && state.editingControlBase) {
          // Update existing control - API will handle versioning if needed
          const result = await controlsService.updateControl(
            state.editingControlBase.id,
            FileTypes.CUSTOM,
            blob
          );
          newControl = result.control;
          action = result.action;
        } else {
          // Create new control
          newControl = await controlsService.uploadControl(
            angle.id,
            FileTypes.CUSTOM,
            blob
          );
          action = 'created';
        }

        // Show appropriate success message based on action
        if (action === 'created' && !state.isCreatingNewControl) {
          // Control was versioned because it's used in renders
          toast.info(t('toastVersionCreated'), {
            description: t('toastVersionCreatedDescription'),
            duration: 6000,
          });
        } else if (action === 'updated') {
          toast.success(t('toastUpdated'));
        } else {
          toast.success(t('toastCreated'));
        }

        // Refresh the project-controls query backing the settings-panel list
        invalidateControlLists(newControl.attributes.project_id);

        // Reload controls in parent component
        if (onControlsUpdate) {
          const freshControls = await controlsService.getControlsForAngle(
            angle.id
          );
          const validControls = filterValidControls(freshControls);
          onControlsUpdate(validControls);
        }

        onControlsChange([{ id: newControl.id }]);

        // Close editor only on successful save
        setState((prev) => ({
          ...prev,
          isEditingControl: false,
          editingControlBase: null,
          isCreatingNewControl: false,
        }));
      } catch (err) {
        console.error('Failed to save control:', err);

        toast.error(t('toastSaveFailed'), {
          description:
            err instanceof ApiClientError
              ? err.getFirstError()
              : tErrors('generic'),
        });
        // Don't close editor on error - keep it open so user can try again
      }
    },
    [
      angle,
      state.isCreatingNewControl,
      state.editingControlBase,
      onControlsUpdate,
      onControlsChange,
      invalidateControlLists,
      t,
      tErrors,
    ]
  );

  const handleDeleteControl = useCallback(
    (control: Control) => {
      if (!angle) return;

      if (control.attributes.is_used_in_renders) {
        toast.warning(t('toastCannotDelete'), {
          description: t('toastUsedInDesigns'),
          duration: 4000,
        });
        return;
      }

      // Deselect control immediately if it's selected
      const isSelected = selectedControlIds.some((item) =>
        typeof item === 'string' ? item === control.id : item.id === control.id
      );
      if (isSelected) {
        onControlsChange([]);
      }

      const isUsed = control.attributes.is_used_in_renders || false;
      setState((prev) => ({
        ...prev,
        deleteConfirmation: { control, isUsed },
      }));
    },
    [angle, selectedControlIds, onControlsChange, t]
  );

  const handleConfirmDeleteControl = useCallback(async () => {
    if (!state.deleteConfirmation || !angle) return;

    const { control } = state.deleteConfirmation;

    setState((prev) => ({ ...prev, deleting: true }));

    try {
      await controlsService.deleteControl(control.id);

      // Keep the settings-panel sketch list in sync with the deletion
      invalidateControlLists(control.attributes.project_id);

      // Reload controls in parent component before closing modal
      if (onControlsUpdate) {
        const freshControls = await controlsService.getControlsForAngle(
          angle.id
        );
        const validControls = filterValidControls(freshControls);
        onControlsUpdate(validControls);
      }

      // Close modal only after controls are refreshed
      setState((prev) => ({
        ...prev,
        deleteConfirmation: null,
        deleting: false,
      }));

      toast.success(t('toastDeleted'));
    } catch (err) {
      console.error('Failed to delete control:', err);

      if (
        err instanceof ApiClientError &&
        'response' in err &&
        (err as { response?: { status?: number } }).response?.status === 409
      ) {
        toast.error(t('toastCannotDelete'), {
          description: t('toastUsedInDesigns'),
        });
      } else {
        toast.error(t('toastDeleteFailed'), {
          description:
            err instanceof ApiClientError
              ? err.getFirstError()
              : tErrors('generic'),
        });
      }

      // Reset deleting state on error, keep modal open
      setState((prev) => ({ ...prev, deleting: false }));
    }
  }, [
    state.deleteConfirmation,
    angle,
    onControlsUpdate,
    invalidateControlLists,
    t,
    tErrors,
  ]);

  const handleCancelDelete = useCallback(() => {
    setState((prev) => ({ ...prev, deleteConfirmation: null }));
  }, []);

  const handleControlToggle = useCallback(
    (controlId: string) => {
      const control = controls.find((c) => c.id === controlId);
      if (!control) return;

      const isSelected = selectedControlIds.some((item) =>
        typeof item === 'string' ? item === controlId : item.id === controlId
      );

      if (isSelected) {
        onControlsChange([]);
      } else {
        onControlsChange([{ id: controlId }]);
      }
    },
    [controls, selectedControlIds, onControlsChange]
  );

  const cancelEditing = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isEditingControl: false,
      editingControlBase: null,
      isCreatingNewControl: false,
    }));
  }, []);

  return {
    ...state,
    controls,
    selectedControl,
    unusedControlsCount,
    hasSelectedControl: selectedControlIds.length > 0,
    // Handlers
    handleCreateControl,
    handleEditControl,
    handleEditControlFor,
    handleSaveEditedControl,
    handleDeleteControl,
    handleConfirmDeleteControl,
    handleCancelDelete,
    handleControlToggle,
    cancelEditing,
  };
}
